import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import site from './site.json';
import pages from './pages.json';
import { applyHead, resetHead, type HeadTag } from './head';
import type { JsonLd } from './jsonLd';
import { absoluteUrl, normalizePath } from './url';

export interface SeoInput {
  /** The full <title>, brand included. */
  title: string;
  description: string;
  /** Path used for the canonical URL. Defaults to the current path. */
  path?: string;
  /** Social preview image: an absolute URL or a site-relative path. Defaults to the site image. */
  image?: string;
  imageAlt?: string;
  /** Keep this page out of search results (e.g. "page not found"). */
  noindex?: boolean;
  jsonLd?: JsonLd[];
}

const DEFAULT_TITLE = (pages as Record<string, { title: string }>)['/'].title;

const meta = (attrs: Record<string, string>): HeadTag => ({ tag: 'meta', attrs });

function buildTags(input: SeoInput, path: string): HeadTag[] {
  const url = absoluteUrl(path);
  const image = absoluteUrl(input.image ?? site.ogImage);
  const imageAlt = input.imageAlt ?? input.title;

  return [
    meta({ name: 'description', content: input.description }),
    { tag: 'link', attrs: { rel: 'canonical', href: url } },
    ...(input.noindex ? [meta({ name: 'robots', content: 'noindex, follow' })] : []),

    // Open Graph: what Facebook, WhatsApp, LinkedIn and others show when the link is shared.
    meta({ property: 'og:type', content: 'website' }),
    meta({ property: 'og:site_name', content: site.name }),
    meta({ property: 'og:locale', content: site.locale }),
    meta({ property: 'og:title', content: input.title }),
    meta({ property: 'og:description', content: input.description }),
    meta({ property: 'og:url', content: url }),
    meta({ property: 'og:image', content: image }),
    meta({ property: 'og:image:alt', content: imageAlt }),

    meta({ name: 'twitter:card', content: 'summary_large_image' }),
    meta({ name: 'twitter:title', content: input.title }),
    meta({ name: 'twitter:description', content: input.description }),
    meta({ name: 'twitter:image', content: image }),

    ...(input.jsonLd ?? []).map(
      (data): HeadTag => ({
        tag: 'script',
        attrs: { type: 'application/ld+json' },
        text: JSON.stringify(data),
      })
    ),
  ];
}

/**
 * Sets the page title, description, canonical URL, social preview tags and structured data,
 * and removes them again when the page goes away. Pass `null` while data is still loading.
 */
export function useSeo(input: SeoInput | null): void {
  const { pathname } = useLocation();

  // Compare by value, so callers can pass a fresh object on every render.
  const key = input ? JSON.stringify(input) : '';
  const stable = useMemo<SeoInput | null>(() => (key ? JSON.parse(key) : null), [key]);

  useEffect(() => {
    if (!stable) return;
    applyHead(stable.title, buildTags(stable, normalizePath(stable.path ?? pathname)));
    return () => resetHead(DEFAULT_TITLE);
  }, [stable, pathname]);
}
